import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Button, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Upload,
  Select,
  Radio,
  Popover,
  message,
  Tag,
} from "antd";
import {
  InboxOutlined,
  SendOutlined,
  EditOutlined,
  ScheduleOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import workExecutionApis from "../../api/WorkOrderManagement/WorkExecutionApis";
import WorkOrderApprovalDrawer from "../work-orders/workOrderCreation/WorkOrderApprovalDrawer";
import sequenceApis from "../../api/master/sequence";
import { WorkType } from "../work-orders/types";

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Dragger } = Upload;

const WorkExecutionUpdate: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [workLines, setWorkLines] = useState<any[]>([]);
  const [approvalRequired, setApprovalRequired] = useState<string>("false");
  const [approvals, setApprovals] = useState<{ open: boolean; data: any[] }>({
    open: false,
    data: [],
  });
  const [sequenceAccess, setSequenceAccess] = useState<any>();
  const [isStrictApproval, setIsStrictApproval] = useState<boolean>(false);

  useEffect(() => {
    fetchSequenceAccess();
    if (workOrderId) {
      loadWorkOrderData(workOrderId);
    }
  }, [workOrderId]);

  const loadWorkOrderData = async (id: string) => {
    try {
      setLoading(true);
      const response: any = await workOrderApis.getWorkOrderById(id);

      if (response && response.status && response.data) {
        const apiData = response.data;
        setWorkOrder(apiData);
        console.log(apiData);

        if (apiData.isStrictApproval !== undefined) {
          setIsStrictApproval(Boolean(apiData.isStrictApproval));
        }
        // Map work order lines for execution
        const mappedLines =
          apiData.workOrderLines?.map((line: any) => ({
            id: line.workOrderLineId?.toString() || "",
            workLineNumber: line.workOrderLineId || "",
            workLineTitle: line.taskTitle || "",
            description: line.description || "",
            approvedQuantity: line.quantity || 0,
            rate: line.rate || 0,
            executedQuantity: 0,
            cumulativeExecuted: line.totalExecutedQty || 0,
            pendingApprovalQty: line.totalApprovalExecutedQty || 0,
            remainingQuantity:
              apiData.workType === "Open"
                ? 0
                : line.quantity -
                line.totalExecutedQty -
                line.totalApprovalExecutedQty,
            percentCompletion: 0,
            remarks: "",
            uom: line.uomName || "",
            workType: apiData.workType, // Include workType for OPEN type handling
            error: "", // Initialize error field for validation
            // Stage breakdown for stage-wise execution
            stages:
              line.stages?.map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                stageTitle: stage.stageTitle || "",
                splitPercentage: stage.splitPercentage || 0,
                approvedQty: stage.allocatedQty || 0,
                cumulativeExecutedQty: stage.totalExecutedQty || 0,
                pendingApprovalQty: stage.totalApprovalExecutedQty || 0,
                remainingQty:
                  stage.allocatedQty > 0
                    ? stage.allocatedQty -
                    (stage.totalExecutedQty + stage.totalApprovalExecutedQty)
                    : 0,
                subtotal: stage.subtotal,
                totalExecutedValue: stage.totalExecutedValueExclTax,
                totalApprovalExecutedValue:
                  stage.totalApprovalExecutedValueExclTax || 0,
                balanceExecutionValue: stage.balanceExecutionValue,
                executedQuantity: 0,
                executedAmount: 0,
                remarks: "",
              })) || [],
          })) || [];
        console.log("mappedLines", mappedLines);
        setWorkLines(mappedLines);
      } else {
        message.error("Failed to load work order data");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps = {
    multiple: true,
    fileList,
    onChange: ({ fileList: newFileList }: any) => setFileList(newFileList),
    beforeUpload: () => false,
    accept: ".pdf,.jpg,.jpeg,.png,.docx",
  };

  const handleExecutedQuantityChange = (lineId: string, value: number) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        // For OPEN work type, don't calculate percentage (approvedQuantity is 0)
        // Just track cumulative executed quantity
        const isOpenType = line.workType === WorkType.Open;

        // Validate: For non-Open types, executed qty should not exceed remaining qty
        const hasError = !isOpenType && value > line.remainingQuantity;

        return {
          ...line,
          executedQuantity: value,
          error: hasError
            ? `Cannot exceed remaining quantity (${line.remainingQuantity})`
            : "",
          percentCompletion: isOpenType
            ? 0 // For OPEN type, percentage is not applicable
            : line.approvedQuantity > 0
              ? Math.round(
                ((line.cumulativeExecuted + value) / line.approvedQuantity) *
                100
              )
              : 0,
        };
      })
    );
  };

  const handleRemarksChange = (lineId: string, value: string) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) =>
        line.id === lineId ? { ...line, remarks: value } : line
      )
    );
  };

  // Handler for stage-wise executed amount change
  const handleStageExecutedQuantityChange = (
    lineId: string,
    stageId: number,
    value: number,
    stageRate: number
  ) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        const updatedStages = line.stages.map((stage: any) =>
          stage.workOrderLineStageId === stageId
            ? {
              ...stage,
              executedQuantity: value,
              executedAmount: value * stageRate, // Calculate Executed Amount = Executed Qty * Stage Rate
            }
            : stage
        );

        return { ...line, stages: updatedStages };
      })
    );
  };

  // Handler for stage-wise remarks change
  const handleStageRemarksChange = (
    lineId: string,
    stageId: number,
    value: string
  ) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        const updatedStages = line.stages.map((stage: any) =>
          stage.workOrderLineStageId === stageId
            ? { ...stage, remarks: value }
            : stage
        );

        return { ...line, stages: updatedStages };
      })
    );
  };

  const fetchSequenceAccess = async () => {
    try {
      const response = await sequenceApis.fetchSequence();
      const { data } = response;
      const today = new Date();
      const _sequenceAccess = data?.filter(
        (item: any) =>
          item.moduleName === "Work Execution" &&
          new Date(item.endDate) > today &&
          item.status === "Active"
      );
      if (_sequenceAccess.length > 0) {
        setSequenceAccess({
          module: "Work Execution",
          level: _sequenceAccess[0].level,
          siteIds: _sequenceAccess?.map((item: any) =>
            item?.siteId?.toString()
          ),
        });
      } else {
        setSequenceAccess({
          module: "",
          level: "",
          siteIds: [],
        });
      }
    } catch (error: any) {
      console.log(error.response?.data?.message || "An error occurred");
    }
  };

  const validateForm = () => {
    const values = form.getFieldsValue();

    if (!values.executionPeriod || values.executionPeriod.length !== 2) {
      message.error("Please select execution period");
      return false;
    }

    // Check for execution data:
    // - Lines WITH stages: check if any stage has executedQuantity > 0
    // - Lines WITHOUT stages: check if executedQuantity > 0
    const hasStageExecution = workLines.some(
      (line) =>
        line.stages &&
        line.stages.length > 0 &&
        line.stages.some((stage: any) => stage.executedQuantity > 0)
    );
    const hasExecutedQuantity = workLines.some(
      (line) =>
        (!line.stages || line.stages.length === 0) && line.executedQuantity > 0
    );

    if (!hasStageExecution && !hasExecutedQuantity) {
      message.error(
        "Please enter executed amount/quantity for at least one work line"
      );
      return false;
    }

    // Check for validation errors in work lines (executed qty exceeding remaining qty)
    const hasValidationErrors = workLines.some((line) => line.error);
    if (hasValidationErrors) {
      message.error(
        "Please fix validation errors in work lines before submitting"
      );
      return false;
    }

    // Validate approval configuration if approval required
    if (approvalRequired === "true" && approvals.data.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Approval Required",
        text: "Please configure approvals before submitting",
      });
      return false;
    }

    // Validate that if strict approval is enabled, approvals are configured
    if (approvalRequired === "true" && isStrictApproval && approvals.data.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Strict Approval Configuration",
        text: "Please configure approvals before enabling strict approval mode",
      });
      return false;
    }
    return true;
  };

  const handleSubmitForApproval = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      const values = form.getFieldsValue();

      // Build lineUpdates based on whether line has stages or not
      const lineUpdates = workLines
        .filter((line) => {
          // Include line if it has stages with executedQuantity > 0
          if (line.stages && line.stages.length > 0) {
            return line.stages.some((stage: any) => stage.executedQuantity > 0);
          }
          // Include line if it has executedQuantity > 0 (no stages)
          return line.executedQuantity > 0;
        })
        .map((line) => {
          // If line has stages, use stage-wise payload
          if (line.stages && line.stages.length > 0) {
            const stagesWithExecution = line.stages
              .filter((stage: any) => stage.executedQuantity > 0)
              .map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                executedQuantity: stage.executedQuantity,
                remarks: stage.remarks || "",
              }));

            return {
              workOrderLineId: line.id,
              stages: stagesWithExecution,
            };
          }
          // If no stages, use current flow with executedQuantity
          return {
            workOrderLineId: line.id,
            executedQuantity: line.executedQuantity,
            remarks: line.remarks || "",
          };
        });

      // Create files data array if files are uploaded
      const filesData = fileList.map((file, index) => ({
        order: index,
        module: "Work Order Management",
        moduleFileType: "Execution Report",
      }));

      // Prepare execution dates from the form
      const executionPeriod = values.executionPeriod;
      const executionStartDate = executionPeriod?.[0]
        ? executionPeriod[0].format("YYYY-MM-DD")
        : "";
      const executionEndDate = executionPeriod?.[1]
        ? executionPeriod[1].format("YYYY-MM-DD")
        : "";

      const workExecutionData = {
        workOrderId: workOrder.workOrderId,
        ...(values.batchNumber && { batchNumber: values.batchNumber }),
        executionStartDate: executionStartDate,
        executionEndDate: executionEndDate,
        approvalsRequired: approvalRequired === "true",
        approvals: approvalRequired === "true" ? approvals.data : [],
        isStrictApproval: approvalRequired === "true" ? isStrictApproval : false,
        lineUpdates: lineUpdates,
        isSelfUpdated: true,
        submissionComments: values.notes || "Work execution update submitted",
        filesData: filesData,
      };

      // Prepare FormData for multipart request
      const formData = new FormData();
      formData.append("workExecution", JSON.stringify(workExecutionData));

      // Add files to form data
      if (fileList.length > 0) {
        fileList.forEach((file, index) => {
          formData.append("files", file.originFileObj || file);
        });
      }

      const response: any = await workExecutionApis.selfCreateWorkExecution(
        formData
      );

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Work execution submitted successfully",
        }).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-view/${workOrderId}${flowTypeQuery}`);
        });
      } else {
        throw new Error(response?.message || "Failed to submit work execution");
      }
    } catch (error: any) {
      console.error("Error submitting work execution:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit work execution. Please try again.";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const executionLinesColumns = [
    {
      title: "Line #",
      dataIndex: "workLineNumber",
      key: "workLineNumber",
      width: 80,
      fixed: "left" as const,
    },
    {
      title: "Work Line Title",
      dataIndex: "workLineTitle",
      key: "workLineTitle",
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
    },
    {
      title: "Approved Qty",
      dataIndex: "approvedQuantity",
      key: "approvedQuantity",
      width: 180,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString("en-IN")} {record.uom}
        </div>
      ),
    },
    {
      title: "Executed Qty",
      dataIndex: "cumulativeExecuted",
      key: "cumulativeExecuted",
      width: 150,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString("en-IN")} {record.uom}
        </div>
      ),
    },
    {
      title: "Pending Approval Qty",
      dataIndex: "pendingApprovalQty",
      key: "pendingApprovalQty",
      width: 150,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString("en-IN")} {record.uom}
        </div>
      ),
    },
    {
      title: "Remaining Qty",
      dataIndex: "remainingQuantity",
      key: "remainingQuantity",
      width: 180,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString("en-IN")} {record.uom}
        </div>
      ),
    },
    {
      title: "Executed Qty",
      key: "executedQuantity",
      width: 220,
      render: (_: any, record: any) => {
        // If line has stages, show indicator to expand for stage-wise entry
        if (record.stages && record.stages.length > 0) {
          const totalStageExecutedQty = record.stages.reduce(
            (sum: number, stage: any) => sum + (stage.executedQuantity || 0),
            0
          );
          const totalStageExecutedAmount = record.stages.reduce(
            (sum: number, stage: any) => sum + (stage.executedAmount || 0),
            0
          );
          return (
            <div style={{ textAlign: "center" }}>
              <Tag color="purple">
                <ScheduleOutlined /> Stage-wise
              </Tag>
              {totalStageExecutedQty > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    color: "#1890ff",
                  }}
                >
                  Qty: {totalStageExecutedQty.toLocaleString("en-IN")}
                </div>
              )}
              {totalStageExecutedAmount > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "2px",
                    color: "#52c41a",
                  }}
                >
                  Amt: ₹{totalStageExecutedAmount.toLocaleString("en-IN")}
                </div>
              )}
            </div>
          );
        }
        // For OPEN work type, allow any quantity (no max limit)
        const isOpenType = record.workType === WorkType.Open;
        return (
          <div>
            <Input
              type="number"
              min={0}
              max={isOpenType ? undefined : record.remainingQuantity}
              value={record.executedQuantity}
              onChange={(e) =>
                handleExecutedQuantityChange(
                  record.id,
                  parseFloat(e.target.value) || 0
                )
              }
              style={{ width: "100%" }}
              addonAfter={record.uom}
              status={record.error ? "error" : undefined}
            />
            {record.error && (
              <div
                style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
              >
                {record.error}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "% Completion",
      dataIndex: "percentCompletion",
      key: "percentCompletion",
      width: 120,
      render: (percent: number, record: any) => {
        // For lines with stages, show "-"
        if (record.stages && record.stages.length > 0) {
          return <span className="text-muted">-</span>;
        }
        return (
          <div style={{ textAlign: "center" }}>
            <span
              className={`badge ${percent >= 100
                ? "bg-success"
                : percent >= 75
                  ? "bg-info"
                  : percent >= 50
                    ? "bg-warning"
                    : "bg-secondary"
                }`}
            >
              {percent}%
            </span>
          </div>
        );
      },
    },
    {
      title: "Remarks",
      key: "remarks",
      width: 200,
      render: (_: any, record: any) => {
        // If line has stages, remarks are entered at stage level
        if (record.stages && record.stages.length > 0) {
          return <span className="text-muted">Expand to enter</span>;
        }
        return (
          <TextArea
            rows={2}
            value={record.remarks}
            onChange={(e) => handleRemarksChange(record.id, e.target.value)}
            placeholder="Enter remarks (optional)"
          />
        );
      },
    },
  ];

  if (loading) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Loading...</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Block>
      </Content>
    );
  }

  if (!workOrder) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Work Order Not Found</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
      </Content>
    );
  }

  return (
    <>
      <Head title="Work Execution Update" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>
                Work Execution Update - {workOrder.workOrderNumber}
              </BlockTitle>
              <div className="nk-block-des text-soft">
                <p>Submit work execution details for approval</p>
              </div>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="light"
                outline
                className="bg-white d-none d-sm-inline-flex"
                onClick={() => navigate(-1)}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              <Form form={form} layout="vertical">
                {/* Header Information */}
                <Row
                  className="g-3"
                  style={{
                    marginBottom: 24,
                    padding: 16,
                    background: "#f5f5f5",
                    borderRadius: 8,
                  }}
                >
                  <Col md="3">
                    <div>
                      <strong>WO #:</strong> {workOrder.workOrderNumber}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Contractor:</strong> {workOrder.contractorName}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Site:</strong> {workOrder.siteName}
                    </div>
                  </Col>
                  <Col md="3">
                    <div>
                      <strong>Total Value:</strong> ₹
                      {workOrder.grossValue?.toLocaleString("en-IN") || "0"}
                    </div>
                  </Col>
                </Row>

                {/* Execution Details */}
                <Row className="g-3 mb-4">
                  {(sequenceAccess?.level === "Site" &&
                    !sequenceAccess?.siteIds?.includes(
                      workOrder?.siteId?.toString()
                    )) ||
                    sequenceAccess?.module === "" ? (
                    <Col md="3">
                      <Form.Item
                        label="Work Execution Number"
                        name="batchNumber"
                        rules={[
                          {
                            required: true,
                            message: "Please enter batch number",
                          },
                        ]}
                      >
                        <Input placeholder="Enter batch number" />
                      </Form.Item>
                    </Col>
                  ) : null}
                  <Col
                    md={
                      sequenceAccess?.level === "Site" &&
                        sequenceAccess?.siteIds?.includes(
                          workOrder?.siteId?.toString()
                        ) &&
                        sequenceAccess?.module !== ""
                        ? "4"
                        : "3"
                    }
                  >
                    <Form.Item
                      label="Execution Period"
                      name="executionPeriod"
                      rules={[
                        {
                          required: true,
                          message: "Please select execution period",
                        },
                      ]}
                    >
                      <RangePicker
                        style={{ width: "100%" }}
                        disabledDate={(current) =>
                          current && current > dayjs().endOf("day")
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col
                    md={
                      sequenceAccess?.level === "Site" &&
                        sequenceAccess?.siteIds?.includes(
                          workOrder?.siteId?.toString()
                        ) &&
                        sequenceAccess?.module !== ""
                        ? "4"
                        : "3"
                    }
                  >
                    <Form.Item
                      label="Approval Required?"
                      required
                      style={{ marginBottom: 0 }}
                    >
                      <Radio.Group
                        value={approvalRequired}
                        onChange={(e) => setApprovalRequired(e.target.value)}
                        style={{ width: "100%" }}
                      >
                        <Radio.Button value="true">Yes</Radio.Button>
                        <Radio.Button value="false">No</Radio.Button>
                      </Radio.Group>

                      {/* Set Approvals Link */}
                      {approvalRequired === "true" && (
                        <div style={{ marginTop: 8 }}>
                          <Popover
                            content="Click here to set required approvals"
                            title="Approval Required"
                          >
                            <a
                              href="#"
                              className="success"
                              onClick={(e) => {
                                e.preventDefault();
                                setApprovals((pre) => ({
                                  ...pre,
                                  open: true,
                                }));
                              }}
                            >
                              <EditOutlined /> Set Approvals &nbsp;
                              {approvals?.data?.length === 0 ? (
                                <span className="mandatory">
                                  Please set approvals
                                </span>
                              ) : (
                                <span className="text-success">
                                  ✓ {approvals.data.length} approval(s)
                                  configured
                                </span>
                              )}
                            </a>
                          </Popover>
                        </div>
                      )}
                    </Form.Item>
                  </Col>
                  <Col
                    md={
                      sequenceAccess?.level === "Site" &&
                        sequenceAccess?.siteIds?.includes(
                          workOrder?.siteId?.toString()
                        ) &&
                        sequenceAccess?.module !== ""
                        ? "4"
                        : "3"
                    }
                  >
                    <Form.Item label="Notes" name="notes">
                      <TextArea
                        rows={3}
                        placeholder="Enter additional notes (optional)"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md="12">
                    <Form.Item label="Attachments" name="attachments">
                      <Dragger {...uploadProps} style={{ padding: "20px" }}>
                        <p
                          className="ant-upload-drag-icon"
                          style={{ marginBottom: "16px" }}
                        >
                          <InboxOutlined
                            style={{ fontSize: "48px", color: "#1890ff" }}
                          />
                        </p>
                        <p
                          className="ant-upload-text"
                          style={{ fontSize: "16px", marginBottom: "8px" }}
                        >
                          Click or drag files to upload
                        </p>
                        <p
                          className="ant-upload-hint"
                          style={{ fontSize: "14px", color: "#999" }}
                        >
                          Support for PDF, JPG, PNG, DOCX files. Multiple files
                          allowed.
                        </p>
                      </Dragger>
                    </Form.Item>
                  </Col>
                </Row>

                {/* Work Lines Summary */}
                {workLines.length > 0 && (
                  <Row
                    style={{
                      marginBottom: "3%",
                      padding: 12,
                      background: "#e6f7ff",
                      borderRadius: 6,
                      marginTop: "2%",
                    }}
                  >
                    <Col md="12">
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <strong>Work Lines Summary:</strong>
                        <div>
                          <span>
                            Total Lines: <strong>{workLines.length}</strong>
                          </span>
                          <span style={{ marginLeft: 20 }}>
                            Total Approved Qty:{" "}
                            <strong>
                              {workLines
                                .reduce(
                                  (sum, line) => sum + line.approvedQuantity,
                                  0
                                )
                                .toLocaleString("en-IN")}
                            </strong>
                          </span>
                          <span style={{ marginLeft: 20 }}>
                            Remaining Qty:{" "}
                            <strong>
                              {workLines
                                .reduce(
                                  (sum, line) => sum + line.remainingQuantity,
                                  0
                                )
                                .toLocaleString("en-IN")}
                            </strong>
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* Work Lines Grid */}
                <div style={{ marginBottom: 16 }}>
                  <h6 style={{ marginBottom: 16 }}>
                    <strong>
                      Work Lines - Execution Update ({workLines.length} lines)
                    </strong>
                  </h6>
                  <Table
                    columns={executionLinesColumns}
                    dataSource={workLines}
                    rowKey="id"
                    pagination={false}
                    scroll={{ x: 1400 }}
                    size="small"
                    bordered
                    expandable={{
                      expandedRowRender: (record: any) => {
                        if (!record.stages || record.stages.length === 0)
                          return null;

                        return (
                          <div
                            style={{
                              padding: "12px 16px",
                              backgroundColor: "#fafafa",
                            }}
                          >
                            <div
                              style={{
                                marginBottom: 12,
                                fontWeight: 600,
                                color: "#722ed1",
                              }}
                            >
                              <ScheduleOutlined style={{ marginRight: 8 }} />
                              Stage-wise Execution ({record.stages.length}{" "}
                              stages)
                            </div>
                            <Table
                              dataSource={record.stages.map(
                                (stage: any, idx: number) => ({
                                  ...stage,
                                  key: idx,
                                  stageAmount:
                                    (record.rate *
                                      (stage.splitPercentage || 0)) /
                                    100,
                                })
                              )}
                              columns={[
                                {
                                  title: "Stage",
                                  dataIndex: "stageTitle",
                                  key: "stageTitle",
                                  width: 200,
                                },
                                {
                                  title: "Percentage",
                                  dataIndex: "splitPercentage",
                                  key: "splitPercentage",
                                  width: 100,
                                  render: (val: number) => (
                                    <Tag color="blue">{val}%</Tag>
                                  ),
                                },
                                {
                                  title: "Stage Rate",
                                  dataIndex: "stageAmount",
                                  key: "stageAmount",
                                  width: 150,
                                  render: (val: number) => (
                                    <span style={{ fontWeight: 500 }}>
                                      ₹
                                      {val?.toLocaleString("en-IN", {
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  ),
                                },
                                {
                                  title: "Approved Qty",
                                  dataIndex: "approvedQty",
                                  key: "approvedQty",
                                  width: 150,
                                  // render: (val: number) => (
                                  //   <span style={{ fontWeight: 500 }}>
                                  //     ₹
                                  //     {val?.toLocaleString("en-IN", {
                                  //       maximumFractionDigits: 2,
                                  //     })}
                                  //   </span>
                                  // ),
                                },
                                {
                                  title: "Executed	Qty",
                                  dataIndex: "cumulativeExecutedQty",
                                  key: "cumulativeExecutedQty",
                                  width: 150,
                                  // render: (val: number) => (
                                  //   <span style={{ fontWeight: 500 }}>
                                  //     ₹
                                  //     {val?.toLocaleString("en-IN", {
                                  //       maximumFractionDigits: 2,
                                  //     })}
                                  //   </span>
                                  // ),
                                },
                                {
                                  title: "Pending Approval Qty",
                                  dataIndex: "pendingApprovalQty",
                                  key: "pendingApprovalQty",
                                  width: 150,
                                  // render: (val: number) => (
                                  //   <span style={{ fontWeight: 500 }}>
                                  //     ₹
                                  //     {val?.toLocaleString("en-IN", {
                                  //       maximumFractionDigits: 2,
                                  //     })}
                                  //   </span>
                                  // ),
                                },
                                {
                                  title: "Remaining	Qty",
                                  dataIndex: "remainingQty",
                                  key: "remainingQty",
                                  width: 150,
                                  // render: (val: number) => (
                                  //   <span style={{ fontWeight: 500 }}>
                                  //     ₹
                                  //     {val?.toLocaleString("en-IN", {
                                  //       maximumFractionDigits: 2,
                                  //     })}
                                  //   </span>
                                  // ),
                                },
                                {
                                  title: "Executed Qty",
                                  key: "executedQuantity",
                                  width: 120,
                                  render: (_: any, stage: any) => (
                                    <InputNumber
                                      min={0}
                                      value={stage.executedQuantity}
                                      onChange={(value) =>
                                        handleStageExecutedQuantityChange(
                                          record.id,
                                          stage.workOrderLineStageId,
                                          value || 0,
                                          stage.stageAmount || 0
                                        )
                                      }
                                      style={{ width: "100%" }}
                                    />
                                  ),
                                },
                                {
                                  title: "Executed Amount",
                                  key: "executedAmount",
                                  width: 150,
                                  render: (_: any, stage: any) => (
                                    <span style={{ fontWeight: 500 }}>
                                      ₹
                                      {(
                                        stage.executedAmount || 0
                                      ).toLocaleString("en-IN", {
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  ),
                                },
                                {
                                  title: "Remarks",
                                  key: "remarks",
                                  width: 200,
                                  render: (_: any, stage: any) => (
                                    <Input
                                      value={stage.remarks}
                                      onChange={(e) =>
                                        handleStageRemarksChange(
                                          record.id,
                                          stage.workOrderLineStageId,
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter remarks"
                                    />
                                  ),
                                },
                              ]}
                              pagination={false}
                              size="small"
                              bordered
                              summary={() => {
                                const totalStageAmount = record.stages.reduce(
                                  (sum: number, s: any) =>
                                    sum +
                                    (record.rate * (s.splitPercentage || 0)) /
                                    100,
                                  0
                                );
                                const totalExecutedQty = record.stages.reduce(
                                  (sum: number, s: any) =>
                                    sum + (s.executedQuantity || 0),
                                  0
                                );
                                const totalExecutedAmount =
                                  record.stages.reduce(
                                    (sum: number, s: any) =>
                                      sum + (s.executedAmount || 0),
                                    0
                                  );
                                return (
                                  <Table.Summary fixed>
                                    <Table.Summary.Row
                                      style={{ backgroundColor: "#e6f7ff" }}
                                    >
                                      {/* Stage */}
                                      <Table.Summary.Cell index={0}>
                                        <strong>Total</strong>
                                      </Table.Summary.Cell>
                                      {/* Percentage */}
                                      <Table.Summary.Cell index={1}>
                                        -
                                      </Table.Summary.Cell>
                                      {/* Stage Rate */}
                                      <Table.Summary.Cell index={2}>
                                        <strong>
                                          ₹
                                          {totalStageAmount.toLocaleString(
                                            "en-IN",
                                            { maximumFractionDigits: 2 }
                                          )}
                                        </strong>
                                      </Table.Summary.Cell>
                                      {/* Approved Qty */}
                                      <Table.Summary.Cell index={3}>
                                        -
                                      </Table.Summary.Cell>
                                      {/* Cumulative Executed Qty */}
                                      <Table.Summary.Cell index={4}>
                                        -
                                      </Table.Summary.Cell>
                                      {/* Pending Approval Qty */}
                                      <Table.Summary.Cell index={5}>
                                        -
                                      </Table.Summary.Cell>
                                      {/* Remaining Qty */}
                                      <Table.Summary.Cell index={6}>
                                        -
                                      </Table.Summary.Cell>
                                      {/* Executed Qty */}
                                      <Table.Summary.Cell index={7}>
                                        <strong style={{ color: "#1890ff" }}>
                                          {totalExecutedQty.toLocaleString(
                                            "en-IN",
                                            { maximumFractionDigits: 2 }
                                          )}
                                        </strong>
                                      </Table.Summary.Cell>
                                      {/* Executed Amount */}
                                      <Table.Summary.Cell index={8}>
                                        <strong style={{ color: "#52c41a" }}>
                                          ₹
                                          {totalExecutedAmount.toLocaleString(
                                            "en-IN",
                                            { maximumFractionDigits: 2 }
                                          )}
                                        </strong>
                                      </Table.Summary.Cell>
                                      {/* Remarks */}
                                      <Table.Summary.Cell index={9}>
                                        -
                                      </Table.Summary.Cell>
                                    </Table.Summary.Row>
                                  </Table.Summary>
                                );
                              }}
                            />
                          </div>
                        );
                      },
                      expandIcon: ({ expanded, onExpand, record }: any) => {
                        const hasStages =
                          record.stages && record.stages.length > 0;
                        if (!hasStages) return null;

                        return expanded ? (
                          <DownOutlined
                            onClick={(e) => onExpand(record, e)}
                            style={{ color: "#722ed1", cursor: "pointer" }}
                          />
                        ) : (
                          <RightOutlined
                            onClick={(e) => onExpand(record, e)}
                            style={{ color: "#722ed1", cursor: "pointer" }}
                          />
                        );
                      },
                      rowExpandable: (record: any) =>
                        !!(record.stages && record.stages.length > 0),
                    }}
                  />
                </div>

                {/* Action Buttons */}
                <div
                  style={{
                    textAlign: "right",
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: 16,
                    marginTop: 16,
                  }}
                >
                  <Button
                    color="light"
                    onClick={() => navigate(-1)}
                    style={{ marginRight: 8 }}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onClick={handleSubmitForApproval}
                    disabled={
                      submitting ||
                      !(
                        // Check for lines with stages having executedQuantity > 0
                        (
                          workLines.some(
                            (line) =>
                              line.stages &&
                              line.stages.length > 0 &&
                              line.stages.some(
                                (s: any) => s.executedQuantity > 0
                              )
                          ) ||
                          // Check for lines without stages having executedQuantity > 0
                          workLines.some(
                            (line) =>
                              (!line.stages || line.stages.length === 0) &&
                              line.executedQuantity > 0
                          )
                        )
                      ) ||
                      workLines.some((line) => line.error)
                    }
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" /> Submitting...
                      </>
                    ) : (
                      <>
                        {/* <SendOutlined />{" "} */}
                        {approvalRequired === "true"
                          ? "Submit for Approval"
                          : "Submit"}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Block>
      </Content>

      {/* Approval Drawer */}
      {approvals.open && (
        <WorkOrderApprovalDrawer
          open={approvals.open}
          onClose={() => setApprovals((pre) => ({ ...pre, open: false }))}
          onSubmit={(data: any, isStrictApprovalFromDrawer: boolean) => {
            setApprovals({
              open: false,
              data: data,
            });
            setIsStrictApproval(isStrictApprovalFromDrawer);
          }}
          initialStrictApproval={isStrictApproval}
          setApprovalSubmissionStatus={(status) => {
            if (status) {
              message.success("Approvals configured successfully");
            }
          }}
          approvalData={approvals.data}
          siteId={workOrder?.siteId || 0}
          moduleName="Work Execution"
        />
      )}
    </>
  );
};

export default WorkExecutionUpdate;
